import * as Font from 'expo-font';
import React, { ComponentClass } from 'react';
import {
  Text,
  TextProps,
  TouchableHighlightProps,
  ViewProps,
  OpaqueColorValue,
  TextStyle,
  ViewStyle,
  ColorValue,
  PixelRatio,
} from 'react-native';

import createIconSet from './vendor/react-native-vector-icons/lib/create-icon-set';
import createIconButtonComponent from './vendor/react-native-vector-icons/lib/icon-button';

export {
  DEFAULT_ICON_COLOR,
  DEFAULT_ICON_SIZE,
} from './vendor/react-native-vector-icons/lib/create-icon-set';

export interface IconProps<GLYPHS extends string> extends TextProps {
  /**
   * Size of the icon, can also be passed as fontSize in the style object.
   *
   * @default 12
   */
  size?: number;

  /**
   * Name of the icon to show
   *
   * See Icon Explorer app
   * {@link https://expo.github.io/vector-icons/}
   */
  name: GLYPHS;

  /**
   * Color of the icon. Can be a string or OpaqueColorValue (returned from
   * PlatformColor(..))
   *
   */
  color?: string | OpaqueColorValue;
}

export interface IconButtonProps<GLYPHS extends string>
  extends IconProps<GLYPHS>,
    ViewProps,
    TouchableHighlightProps {
  /**
   * Text and icon color
   * Use iconStyle or nest a Text component if you need different colors.
   * Can be a string or OpaqueColorValue (returned from PlatformColor(..))
   *
   *  @default 'white'
   */
  color?: string | OpaqueColorValue;

  /**
   * Border radius of the button
   * Set to 0 to disable.
   *
   * @default 5
   */
  borderRadius?: number;

  /**
   * Styles applied to the icon only
   * Good for setting margins or a different color.
   *
   * @default {marginRight: 10}
   */
  iconStyle?: TextStyle;

  /**
   * Style prop inherited from TextProps and TouchableWithoutFeedbackProperties
   * Only exist here so we can have ViewStyle or TextStyle
   *
   */
  style?: ViewStyle | TextStyle;

  /**
   * Background color of the button. Can be a string or OpaqueColorValue (returned from
   * PlatformColor(..))
   *
   * @default '#007AFF'
   */
  backgroundColor?: string | OpaqueColorValue;
}

export type GlyphMap<G extends string> = { [K in G]: number | string };

export type ImageSource = { uri: string; width: number; height: number; scale: number };

export interface Icon<G extends string, FN extends string> {
  defaultProps: any;
  Button: ComponentClass<IconButtonProps<G>>;
  glyphMap: GlyphMap<G>;
  getRawGlyphMap: () => GlyphMap<G>;
  getFontFamily: () => FN;
  getImageSource: (name: G, size: number, color: ColorValue) => Promise<ImageSource | null>;
  loadFont: () => Promise<void>;
  font: { [x: string]: any };
  new (props: IconProps<G>): React.Component<IconProps<G>>;
}

export default function <G extends string, FN extends string>(
  glyphMap: GlyphMap<G>,
  fontName: FN,
  expoAssetId,
  fontStyle?: any
): Icon<G, FN> {
  const font = { [fontName]: expoAssetId };
  const RNVIconComponent = createIconSet(glyphMap, fontName, null, fontStyle);
  let didWarn = false;

  return class Icon extends React.Component<IconProps<G>> {
    static defaultProps = RNVIconComponent.defaultProps;
    static Button = createIconButtonComponent(Icon);
    static glyphMap = glyphMap;
    static getRawGlyphMap = () => glyphMap;
    static getFontFamily = () => fontName;
    static loadFont = () => Font.loadAsync(font);
    static font = font;
    static getImageSource = async (
      name: G,
      size: number,
      color: ColorValue
    ): Promise<ImageSource | null> => {
      if (__DEV__ && !(name in glyphMap)) {
        console.warn(`"${name}" is not a valid icon name for family "${fontName}"`);
        return null;
      }
      if (typeof Font.renderToImageAsync !== 'function') {
        console.warn(`Font.renderToImageAsync is not available. Please update expo-font.`);
        return null;
      }
      await Font.loadAsync(font);
      const imagePathAndDimensions = await Font.renderToImageAsync(
        String.fromCodePoint(glyphMap[name] as number),
        {
          fontFamily: fontName,
          color: color as string,
          size,
        }
      );
      if (typeof imagePathAndDimensions === 'string') {
        if (__DEV__ && !didWarn) {
          didWarn = true;
          console.warn(
            '@expo/vector-icons: Font.renderToImageAsync() did not return image dimensions, because an outdated version of expo-font was used. The reported width and height are estimates, instead of real image dimension. Update expo-font to resolve this.'
          );
        }
        const dimensions = size;
        return {
          uri: imagePathAndDimensions,
          width: dimensions,
          height: dimensions,
          scale: PixelRatio.get(),
        };
      } else {
        const { uri, width, height } = imagePathAndDimensions;
        return { uri, width, height, scale: PixelRatio.get() };
      }
    };

    _mounted = false;
    _icon?: any;

    state = {
      fontIsLoaded: Font.isLoaded(fontName),
    };

    async componentDidMount() {
      this._mounted = true;
      if (!this.state.fontIsLoaded) {
        await Font.loadAsync(font);
        /* eslint-disable react/no-did-mount-set-state */
        this._mounted && this.setState({ fontIsLoaded: true });
      }
    }

    componentWillUnmount() {
      this._mounted = false;
    }

    setNativeProps(props) {
      if (this._icon) {
        this._icon.setNativeProps(props);
      }
    }

    render() {
      if (__DEV__ && this.props.name && !(this.props.name in glyphMap)) {
        console.warn(`"${this.props.name}" is not a valid icon name for family "${fontName}"`);
      }

      if (!this.state.fontIsLoaded) {
        return <Text />;
      }

      return (
        <RNVIconComponent
          ref={(view) => {
            this._icon = view;
          }}
          {...this.props}
        />
      );
    }
  };
}
